import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { a as unwatchIcon, w as watchIcon, g as getLibrary } from './icon-library-registry.js';

const iconFiles = new Map();
const requestIcon = (url) => {
  if (iconFiles.has(url)) {
    const iconFile = iconFiles.get(url);
    if (iconFile) {
      return iconFile;
    }
  }
  const request = fetch(url).then(async (response) => {
    if (response.ok) {
      const div = document.createElement('div');
      div.innerHTML = await response.text();
      const svg = div.firstElementChild;
      return {
        ok: response.ok,
        status: response.status,
        svg: svg && svg.tagName.toLowerCase() === 'svg' ? svg.outerHTML : '',
      };
    }
    else {
      return {
        ok: response.ok,
        status: response.status,
        svg: null,
      };
    }
  });
  iconFiles.set(url, request);
  return request;
};

const iconCss = ":host{position:relative;box-sizing:border-box}:host *,:host *:before,:host *:after{box-sizing:inherit}:host{display:inline-block;width:1em;min-width:1em;height:1em;contain:strict;box-sizing:content-box !important}.icon,svg{display:block;height:100%;width:100%}";

const parser = new DOMParser();
const Icon = /*@__PURE__*/ proxyCustomElement(class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.__attachShadow();
    this.ttLoad = createEvent(this, "tt-load", 7);
    this.ttError = createEvent(this, "tt-error", 7);
    this.operationId = 0;
    this.svg = undefined;
    this.name = undefined;
    this.src = undefined;
    this.library = '23';
  }
  handleChange() {
    this.setIcon();
  }
  connectedCallback() {
    watchIcon(this.element);
  }
  componentDidLoad() {
    this.setIcon();
  }
  disconnectedCallback() {
    unwatchIcon(this.element);
  }
  /** @internal Fetches the icon and redraws it. Used to handle library registrations. */
  async redraw() {
    this.setIcon();
  }
  async setIcon() {
    var _a;
    const currentOperation = ++this.operationId;
    const library = getLibrary(this.library);
    let url = this.src;
    if (this.name && library) {
      url = library.resolver(this.name);
    }
    if (url) {
      try {
        const file = await requestIcon(url);
        if (currentOperation !== this.operationId)
          return;
        if (file.ok) {
          const doc = parser.parseFromString((_a = file.svg) !== null && _a !== void 0 ? _a : '', 'text/html');
          const svg = doc.body.querySelector('svg');
          if (svg) {
            if (library && library.mutator) {
              library.mutator(svg);
            }
            this.svg = svg.outerHTML;
            this.ttLoad.emit();
          }
          else {
            this.svg = '';
            this.ttError.emit({ status: file.status });
          }
        }
        else {
          this.ttError.emit({ status: file.status });
        }
      }
      catch (_b) {
        this.ttError.emit();
      }
    }
    else if (this.svg) {
      // If we can't resolve a URL and an icon was previously set, remove it
      this.svg = null;
    }
  }
  render() {
    var _a;
    return h("div", { part: "base", class: "icon", role: "presentation", "aria-hidden": "true", innerHTML: (_a = this.svg) !== null && _a !== void 0 ? _a : '' });
  }
  static get assetsDirs() { return ["libraries/twentythree", "libraries/bootstrap", "libraries/one"]; }
  get element() { return this; }
  static get watchers() { return {
    "name": ["handleChange"],
    "src": ["handleChange"],
    "library": ["handleChange"]
  }; }
  static get style() { return iconCss; }
}, [1, "tt-icon", {
    "name": [1],
    "src": [1],
    "library": [1],
    "svg": [32],
    "redraw": [64]
  }]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["tt-icon"];
  components.forEach(tagName => { switch (tagName) {
    case "tt-icon":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, Icon);
      }
      break;
  } });
}

export { Icon as I, defineCustomElement as d };
